import { useEffect } from "react";
import { Link } from "@tanstack/react-router";
import {
  ArrowRight,
  Check,
  ClipboardList,
  Eye,
  FileText,
  HardHat,
  MessagesSquare,
} from "lucide-react";
import { SiteFooter } from "@/components/SiteFooter";
import { Img, SIZES } from "@/components/Img";
import { SikkerhedstjekForm } from "@/components/SikkerhedstjekForm";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { captureAttribution, pushEvent } from "@/lib/tracking";
import logoWhite from "@/assets/mi-support-logo-white.svg";
import julieKontor3 from "@/assets/julie-kontor3.jpg";
import kontorGruppe43 from "@/assets/Kontor-gruppe-4-3.jpg.asset.json";
import gruppe1 from "@/assets/gruppe1.jpg.asset.json";
import danContainer3 from "@/assets/Dan-Container3.jpg.asset.json";
import sikkerhedstjekHero from "@/assets/sikkerhedstjek-hero.jpg.asset.json";

// Lovable assets are mirrored into public/__l5e so they are served from our own domain.
const HERO_IMG = sikkerhedstjekHero.url;
const DAN_IMG = danContainer3.url;

type CtaLocation = "hero" | "content" | "final" | "sticky_mobile";

function scrollToForm(targetId: string) {
  const el = document.getElementById(targetId);
  if (!el) return;
  el.scrollIntoView({ behavior: "smooth", block: "start" });
  el.querySelector<HTMLElement>("input,select,textarea")?.focus({ preventScroll: true });
}

function CtaButton({
  location,
  children,
  className = "",
  formTarget = "ansoeg",
}: {
  location: CtaLocation;
  children: React.ReactNode;
  className?: string;
  formTarget?: string;
}) {
  return (
    <button
      type="button"
      onClick={() => {
        pushEvent("safety_check_cta_click", {
          cta_location: location,
          page_path: typeof window !== "undefined" ? window.location.pathname : "",
        });
        scrollToForm(formTarget);
      }}
      className={`btn-cta min-h-[48px] ${className}`}
    >
      {children}
      <ArrowRight className="h-4 w-4" aria-hidden="true" />
    </button>
  );
}

const TRUST_POINTS = [
  "Gratis og uforpligtende",
  "Gennemgang af jeres byggeplads",
  "Rapport med konkrete anbefalinger",
];

const STEPS = [
  {
    num: "01",
    icon: ClipboardList,
    title: "Ansøgningsskema",
    text: "I udfylder vores korte ansøgningsskema med de vigtigste oplysninger om jeres projekt, byggeplads og eventuelle aktuelle sikkerhedsudfordringer. Vi gennemgår jeres svar grundigt, så vi kan sætte jer op med den rette HSE-specialist.",
    img: julieKontor3,
  },
  {
    num: "02",
    icon: MessagesSquare,
    title: "Indledende dialog",
    text: "Når vi har gennemgået jeres ansøgning, kontakter vi jer for en kort indledende dialog. Her taler vi om projektets rammer, jeres erfaringer og eventuelle områder, hvor I oplever særlige udfordringer med sikkerhed og arbejdsmiljø. Sammen aftaler vi tidspunktet for sikkerhedstjekket.",
    img: kontorGruppe43.url,
    imgClass: "object-[50%_35%]",
  },
  {
    num: "03",
    icon: HardHat,
    title: "Gennemgang af pladsen",
    text: "En af vores erfarne HSE-specialister kommer ud på byggepladsen og gennemfører en grundig og systematisk gennemgang af sikkerheden. Fokus er på at identificere både eksisterende risici og muligheder for at styrke sikkerheden i hverdagen.",
    img: "/wp-content/uploads/MISupportIT_NTK2628_1350x900-1.jpg",
  },
  {
    num: "04",
    icon: FileText,
    title: "Rapport og anbefalinger",
    text: "Efter besøget samler vi vores observationer i en overskuelig rapport. Her får I et konkret overblik over de forhold, vi har observeret, samt anbefalinger til, hvor og hvordan sikkerheden kan forbedres. Rapporten giver jer et praktisk udgangspunkt for det videre sikkerhedsarbejde på byggepladsen.",
    img: gruppe1.url,
    imgClass: "object-[50%_20%]",
  },
];

const FOCUS_AREAS = [
  "Arbejde i højden og faldsikring",
  "Opstilling og brug af stilladser",
  "Gravearbejder og skredsikring",
  "Kranløft og tunge løft",
  "Maskiner og tekniske hjælpemidler",
  "Midlertidig el og kabelføring",
  "Brandfare og varmt arbejde",
  "Særligt farligt arbejde",
  "Støj, støv og sundhedsfarlige stoffer",
  "Orden, ryddelighed og afspærring",
  "Sikkerhedsorganisering og dokumentation",
  "Beredskab og evakuering",
];

const OUTCOMES = [
  {
    icon: Eye,
    title: "Et nyt blik på byggepladsen",
    text: "Få identificeret forhold, som den daglige organisation måske er blevet blind for.",
  },
  {
    icon: ClipboardList,
    title: "Konkrete forbedringsforslag",
    text: "Ikke en generisk checkliste, men observationer fra den aktuelle byggeplads.",
  },
  {
    icon: MessagesSquare,
    title: "Et bedre grundlag for dialog",
    text: "Rapporten kan bruges som input til det videre sikkerheds- og arbejdsmiljøarbejde.",
  },
];

const PROCESS = [
  {
    num: "1",
    title: "I udfylder ansøgningsskemaet",
    text: "I udfylder ansøgningsskemaet, og vi sætter jer op med den rette HSE-specialist.",
  },
  {
    num: "2",
    title: "Vi kontakter jer",
    text: "Vi tager en kort indledende dialog om jeres sikkerhedsudfordringer og aftaler tidspunkt for sikkerhedstjekket.",
  },
  {
    num: "3",
    title: "Vi gennemgår byggepladsen",
    text: "En erfaren HSE-specialist udfører en grundig og systematisk gennemgang af sikkerheden på pladsen.",
  },
  {
    num: "4",
    title: "I modtager rapport og anbefalinger",
    text: "Efter besøget får I en overskuelig rapport med observationer og konkrete forbedringsforslag.",
  },
];

export const FAQ = [
  {
    q: "Koster sikkerhedstjekket noget?",
    a: "Nej. Det udvalgte sikkerhedstjek er gratis og uforpligtende.",
  },
  {
    q: "Er vi forpligtet til at købe noget bagefter?",
    a: "Nej. Gennemgangen indebærer ingen købsforpligtelse.",
  },
  {
    q: "Hvem gennemfører sikkerhedstjekket?",
    a: "En erfaren HSE-specialist/sikkerhedskoordinator fra MI Support.",
  },
  {
    q: "Hvad modtager vi efter besøget?",
    a: "I modtager en rapport med relevante observationer og konkrete forbedringsforslag. Alle oplysninger om jeres projekt og virksomhed behandles fortroligt og videregives ikke til tredjepart.",
  },
  {
    q: "Kan alle få et gratis sikkerhedstjek?",
    a: "Vi tilbyder gratis sikkerhedstjek til udvalgte projekter, der matcher vores kompetencer og faglige fokus.",
  },
];

export function SikkerhedstjekLandingPage() {
  useEffect(() => {
    captureAttribution();
  }, []);

  return (
    <div className="min-h-screen bg-background text-foreground">
      {/* Minimal sticky header */}
      <header className="sticky top-0 z-50 border-b border-white/10 bg-background/90 backdrop-blur">
        <div className="mx-auto flex max-w-7xl items-center justify-between gap-4 px-4 py-3 md:px-8">
          <Link to="/" aria-label="MI Support – forside">
            <img src={logoWhite} alt="MI Support" width={140} height={29} className="h-7 w-auto" />
          </Link>
          <nav aria-label="Sektioner" className="hidden items-center gap-8 text-sm lg:flex">
            <a href="#om-tjekket" className="hover:text-primary">
              Om sikkerhedstjekket
            </a>
            <a href="#saadan-foregaar-det" className="hover:text-primary">
              Sådan foregår det
            </a>
            <a href="#faq" className="hover:text-primary">
              FAQ
            </a>
          </nav>
          <CtaButton location="content" className="px-5 py-2.5 text-sm">
            Ansøg gratis
          </CtaButton>
        </div>
      </header>

      <main className="pb-24 lg:pb-0">
        {/* Hero */}
        <section className="relative overflow-hidden border-b border-white/10">
          <div className="absolute inset-0">
            <Img
              src={HERO_IMG}
              alt="MI Support-medarbejder i sikkerhedsudstyr ved havnefronten"
              className="h-full w-full object-cover object-[68%_50%]"
              sizes={SIZES.full}
              priority
            />
            <div className="absolute inset-0 bg-gradient-to-r from-background via-background/90 to-background/30" />
          </div>
          <div className="relative mx-auto max-w-7xl px-4 py-20 md:px-8 md:py-28 lg:py-32">
            <div className="max-w-2xl">
              <p className="text-sm font-semibold uppercase tracking-widest text-primary">
                Gratis sikkerhedstjek
              </p>
              <h1 className="mt-4 text-4xl font-bold leading-tight tracking-tight md:text-5xl xl:text-6xl">
                Få et gratis sikkerhedstjek af byggepladsen
              </h1>
              <p className="mt-5 text-lg text-foreground/80 md:text-xl">
                Få en professionel vurdering af sikkerheden på jeres byggeprojekt.
              </p>
              <p className="mt-5 max-w-xl leading-relaxed text-foreground/80">
                MI Supports erfarne HSE-specialister gennemgår byggepladsen sammen med jer.
                Efter besøget modtager I en rapport med observationer, risici og
                forbedringsforslag.
              </p>

              <ul className="mt-8 space-y-3">
                {TRUST_POINTS.map((t) => (
                  <li key={t} className="flex items-start gap-3">
                    <span className="mt-0.5 inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-primary/15 text-primary">
                      <Check className="h-3.5 w-3.5" aria-hidden="true" />
                    </span>
                    <span className="text-foreground/90">{t}</span>
                  </li>
                ))}
              </ul>

              <div className="mt-9">
                <CtaButton location="hero" formTarget="hurtig-ansoegning">
                  Ansøg om et gratis sikkerhedstjek
                </CtaButton>
                <p className="mt-3 text-sm text-foreground/65">Det tager ca. 1 minut at ansøge.</p>
              </div>
            </div>
          </div>
        </section>

        {/* Ansøgning (samme formular som nederst) */}
        <section id="hurtig-ansoegning" className="scroll-mt-24 border-b border-white/10 py-16 md:py-20">
          <div className="mx-auto max-w-4xl px-4 md:px-8">
            <div className="text-center">
              <h2 className="text-3xl font-bold tracking-tight md:text-4xl">
                Ansøg om et gratis sikkerhedstjek
              </h2>
              <p className="mx-auto mt-5 max-w-2xl leading-relaxed text-foreground/80">
                Fortæl os kort om jeres byggeprojekt. Når vi har modtaget jeres ansøgning,
                kontakter vi jer for at aftale næste skridt.
              </p>
            </div>
            <div className="mt-8 rounded-3xl border border-white/10 bg-secondary/40 p-6 md:p-9">
              <SikkerhedstjekForm />
            </div>
          </div>
        </section>

        {/* Sådan foregår det */}
        <section
          id="saadan-foregaar-det"
          className="scroll-mt-24 border-y border-white/10 bg-secondary/30 py-20 md:py-24"
        >
          <div className="mx-auto max-w-7xl px-4 md:px-8">
            <h2 className="text-3xl font-bold tracking-tight md:text-4xl">Sådan foregår det</h2>
            <ol className="mt-12 grid gap-8 md:grid-cols-2 lg:grid-cols-4">
              {PROCESS.map((p) => (
                <li key={p.num} className="border-t border-primary/50 pt-6">
                  <span className="text-3xl font-bold text-primary">{p.num}</span>
                  <h3 className="mt-3 text-xl font-bold">{p.title}</h3>
                  <p className="mt-3 leading-relaxed text-foreground/80">{p.text}</p>
                </li>
              ))}
            </ol>
          </div>
        </section>

        {/* Hvad ser vi på? */}
        <section className="bg-section-light py-20 text-section-light-foreground md:py-24">
          <div className="mx-auto max-w-7xl px-4 md:px-8">
            <h2 className="text-3xl font-bold tracking-tight md:text-4xl">Vi ser blandt andet på</h2>
            <ul className="mt-10 grid gap-3 sm:grid-cols-2 md:grid-cols-3">
              {FOCUS_AREAS.map((a) => (
                <li
                  key={a}
                  className="flex items-start gap-3 rounded-xl bg-white/60 px-5 py-4 leading-snug"
                >
                  <Check className="mt-0.5 h-4 w-4 shrink-0 text-primary" aria-hidden="true" />
                  <span>{a}</span>
                </li>
              ))}
            </ul>
            <p className="mt-8 text-sm opacity-80">
              Gennemgangen tilpasses altid projektets karakter, fase og risikobillede.
            </p>
          </div>
        </section>

        {/* Outcomes */}
        <section className="bg-secondary/60 py-20 md:py-24">
          <div className="mx-auto max-w-7xl px-4 md:px-8">
            <h2 className="max-w-3xl text-3xl font-bold tracking-tight md:text-4xl">
              Hvad får I ud af sikkerhedstjekket?
            </h2>
            <div className="mt-12 grid gap-8 md:grid-cols-3">
              {OUTCOMES.map((o) => (
                <div key={o.title}>
                  <span className="inline-flex h-12 w-12 items-center justify-center rounded-xl bg-primary/15 text-primary">
                    <o.icon className="h-6 w-6" aria-hidden="true" />
                  </span>
                  <h3 className="mt-5 text-xl font-bold">{o.title}</h3>
                  <p className="mt-3 leading-relaxed text-foreground/80">{o.text}</p>
                </div>
              ))}
            </div>
            <div className="mt-12">
              <CtaButton location="content">Ansøg om et gratis sikkerhedstjek</CtaButton>
            </div>
          </div>
        </section>

        {/* Trust strip */}
        <section>
          <div className="mx-auto flex max-w-7xl flex-col gap-6 px-4 py-8 md:flex-row md:items-center md:justify-between md:px-8">
            <p className="text-lg font-semibold">Mere end 25 års erfaring fra byggebranchen</p>
            <ul className="flex flex-wrap gap-x-8 gap-y-2 text-sm text-foreground/75">
              <li>HSE-ledelse</li>
              <li>Sikkerhedskoordinering</li>
              <li>Komplekse byggeprojekter</li>
            </ul>
          </div>
        </section>

        {/* Troværdighed */}
        <section className="py-20 md:py-24">
          <div className="mx-auto grid max-w-7xl gap-12 px-4 md:grid-cols-2 md:items-center md:px-8">
            <div>
              <h2 className="text-3xl font-bold tracking-tight md:text-4xl">
                Erfarne sikkerhedsspecialister fra MI Support
              </h2>
              <p className="mt-6 leading-relaxed text-foreground/85">
                MI Support er en dansk ingeniør- og rådgivningsvirksomhed med mere end 25 års
                 erfaring i byggebranchen. Vores sikkerhedsledere og sikkerhedskoordinatorer
                arbejder med HSE på tværs af byggeriets faser – fra projektering og PSS til
                runderinger, risikovurdering, koordinering og sikkerhedsarbejdet under udførelsen.
              </p>
              <figure className="mt-8 border-l-2 border-primary pl-6">
                <blockquote className="text-lg italic leading-relaxed text-foreground/90">
                  “Et sikkerhedstjek udført af mig og mine kolleger giver jer et ærligt og konkret overblik over
                  sikkerheden på byggepladsen. Vi kommer ud til jer, går pladsen igennem sammen
                  med jeres egne folk og peger på de steder, hvor enkle justeringer kan forebygge
                  de fleste ulykker.”
                </blockquote>



                <figcaption className="mt-4 text-sm text-foreground/70">
                  <span className="font-semibold text-foreground">Dan Steen Larsen</span>
                  <br />
                  Sikkerhedskoordinator, MI Support
                </figcaption>
              </figure>
            </div>
            <Img
              src={DAN_IMG}
              alt="Dan Steen Larsen, sikkerhedskoordinator hos MI Support, på en byggeplads"
              className="w-full rounded-3xl object-cover object-[12%_50%] aspect-[4/3]"
              sizes={SIZES.split}
            />
          </div>
        </section>

        {/* Hvad får I? */}
        <section id="om-tjekket" className="scroll-mt-24 bg-secondary/30 py-20 md:py-24">
          <div className="mx-auto max-w-7xl px-4 md:px-8">
            <div className="mx-auto max-w-3xl text-center">
              <h2 className="text-3xl font-bold tracking-tight md:text-4xl">
                En professionel vurdering af jeres byggeplads
              </h2>
              <p className="mt-5 text-lg leading-relaxed text-foreground/80">
                Selv på veldrevne byggepladser kan daglig drift og fremdrift gøre det svært at se de
                forhold, man arbejder med hver dag. Vores sikkerhedstjek giver jer en ekstern,
                faglig vurdering af den aktuelle situation.
              </p>
            </div>

            <ol className="mt-12 grid gap-6 sm:grid-cols-2">
              {STEPS.map((s) => (
                <li
                  key={s.num}
                  className="group flex flex-col overflow-hidden rounded-2xl border border-white/10 bg-secondary/40 transition-colors hover:border-primary/40"
                >
                  <div className="relative shrink-0 overflow-hidden">
                    <Img
                      src={s.img}
                      alt={s.title}
                      sizes={SIZES.card3}
                      className={`h-44 w-full object-cover transition-transform duration-500 group-hover:scale-110 ${"imgClass" in s ? s.imgClass : ""}`}
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-secondary/60 to-transparent" />
                  </div>
                  <div className="flex flex-1 flex-col p-7">
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-semibold tracking-widest text-primary">
                        {s.num}
                      </span>
                      <s.icon className="h-5 w-5 text-foreground/50" aria-hidden="true" />
                    </div>
                    <h3 className="mt-5 text-lg font-bold">{s.title}</h3>
                    <p className="mt-3 text-sm leading-relaxed text-foreground/75">{s.text}</p>
                  </div>
                </li>
              ))}
            </ol>
          </div>
        </section>

        {/* Lead form */}
        <section id="ansoeg" className="scroll-mt-20 py-20 md:py-24">
          <div className="mx-auto max-w-4xl px-4 md:px-8">
            <div className="text-center">
              <h2 className="text-3xl font-bold tracking-tight md:text-4xl">
                Ansøg om et gratis sikkerhedstjek
              </h2>
              <p className="mx-auto mt-5 max-w-2xl leading-relaxed text-foreground/80">
                Fortæl os kort om jeres byggeprojekt. Når vi har modtaget jeres ansøgning,
                kontakter vi jer for at aftale næste skridt.
              </p>
            </div>
            <div className="mt-8 rounded-3xl border border-white/10 bg-secondary/40 p-6 md:p-9">
              <SikkerhedstjekForm />
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section id="faq" className="scroll-mt-24 bg-secondary/30 py-20 md:py-24">
          <div className="mx-auto max-w-3xl px-4 md:px-8">
            <h2 className="text-3xl font-bold tracking-tight md:text-4xl">
              Ofte stillede spørgsmål
            </h2>
            <Accordion type="single" collapsible className="mt-10">
              {FAQ.map((f, i) => (
                <AccordionItem key={f.q} value={`faq-${i}`}>
                  <AccordionTrigger className="text-left text-base font-semibold">
                    {f.q}
                  </AccordionTrigger>
                  <AccordionContent className="text-foreground/80">{f.a}</AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </section>

        {/* Final CTA */}
        <section className="py-20 md:py-28">
          <div className="mx-auto max-w-3xl px-4 text-center md:px-8">
            <h2 className="text-3xl font-bold tracking-tight md:text-4xl">
              Book en gratis gennemgang af jeres byggeprojekt
            </h2>
            <p className="mt-5 text-lg text-foreground/80">
              Ansøg om et gratis og uforpligtende sikkerhedstjek.
            </p>
            <div className="mt-9 flex justify-center">
              <CtaButton location="final">Ansøg om gratis sikkerhedstjek</CtaButton>
            </div>
            <p className="mt-4 text-sm text-foreground/65">
              Gratis · Uforpligtende · For udvalgte projekter
            </p>
          </div>
        </section>
      </main>

      <SiteFooter />

      {/* Sticky mobile CTA */}
      <div className="fixed inset-x-0 bottom-0 z-50 border-t border-white/10 bg-background/95 p-3 backdrop-blur lg:hidden">
        <CtaButton location="sticky_mobile" className="w-full justify-center">
          Ansøg om gratis sikkerhedstjek
        </CtaButton>
      </div>
    </div>
  );
}
